import React from 'react';
import { AddIcon, Card, Input, TrashIcon } from '@avila-tek/ui/src';
import { ACTIONS } from './reducer';
import { Variant } from '../../models';
import { DocumentForm, ExcelForm } from '../shared';
import { useCreateProductAndVariant } from '../../hooks';
import CodeForm from './code/CodeForm';

interface VariantFormProps {
  _id: string;
  option1?: string;
  option2?: string;
  option3?: string;
  updateVariants?: React.Dispatch<React.SetStateAction<Variant[]>>;
}

function VariantForm({
  _id = '',
  option1 = '',
  option2 = '',
  option3 = '',
  updateVariants,
}: VariantFormProps) {
  const id = React.useMemo(() => _id, [_id]) as string;
  const variant = React.useMemo(
    () => ({ option1, option2, option3 }),
    [option1, option2, option3]
  );

  const {
    // States
    formState,
    disabled,
    setDisabled,
    addNewCodes,
    codesArray,
    setCodesArray,
    expirationDate,
    documents,
    setDocuments,

    // Handlers functions
    handleChangeString,
    handleChangeNumber,
    handleExpitationDateInputChange,
    handleSetIsOnline,
    handleChangeAddNewCodes,
    handleExcelDocument,
    handleExpirationDateInputChange,
  } = useCreateProductAndVariant();

  React.useEffect(() => {
    const electronicCodes = codesArray.map((code) => ({
      expirationDate: formState.expirationDate,
      code,
    }));

    updateVariants((_variants) => {
      const [element] = _variants.filter(
        (variantItem) => variantItem._id === id
      );
      const newElement = {
        ...element,
        sku: formState.sku,
        price: formState.price ? Number(formState.price) : null,
        compareAtPrice: formState.compareAtPrice
          ? Number(formState.compareAtPrice)
          : null,
        agreedPrice: formState.agreedPrice
          ? Number(formState.agreedPrice)
          : null,
        codesAutogenerated: !addNewCodes,
        // If addNewCodes is true, then the stock is the length of the codesArray
        stock:
          codesArray.length > 0
            ? codesArray.length
            : Number(formState.stock) || null,
        // If corresArray is not empty, then set the codes property
        electronicCodes: electronicCodes.length > 0 ? electronicCodes : [],
        active: !disabled,
        photos:
          documents.length > 0
            ? documents.map((img) => ({
                src: img.src,
                alt: img.name,
              }))
            : null,
        isOnline: formState.isOnline,
      } as Variant;
      return [
        ..._variants.filter((variantItem) => variantItem._id !== id),
        newElement,
      ];
    });
  }, [id, formState, updateVariants, disabled, documents, codesArray]);

  const toggle = () => setDisabled(!disabled);

  const inputContainerClass = 'px-2 py-1 w-full';

  return (
    <>
      <Card.Header>
        Variante:{' '}
        {Object.values(variant)
          .filter((x) => !!x)
          .join('/')}
      </Card.Header>
      <Card.Body>
        <div className="w-full relative px-5 pt-5 grid md:grid-cols-2 justify-center items-center">
          <section className="flex flex-row justify-center items-center absolute right-0 -top-5">
            <div className="flex flex-row justify-center items-center gap-2 mr-2">
              <div className="flex flex-row justify-center items-center gap-2">
                <span>Compra online</span>
                <input
                  type="checkbox"
                  name={ACTIONS.IS_ONLINE}
                  checked={formState.isOnline}
                  onChange={handleSetIsOnline}
                  className="w-4 h-4 cursor-pointer bg-white rounded border-purple-200 focus:ring-purple-300 focus:ring-2"
                />
              </div>
              <div className="flex flex-row justify-center items-center gap-2">
                <span>Códigos de descuento</span>
                <input
                  type="checkbox"
                  checked={addNewCodes}
                  onChange={() => handleChangeAddNewCodes()}
                  className="w-4 h-4 cursor-pointer bg-white rounded border-purple-200 focus:ring-purple-300 focus:ring-2"
                />
              </div>
            </div>
            <span className="w-[1px] h-[15px] bg-gray-200 m-0" />
            <button
              type="button"
              className={`bg-transparent focus:outline-none outline-none ${
                disabled ? 'text-success-300' : 'text-danger-200'
              }`}
              onClick={toggle}
            >
              {disabled ? (
                <AddIcon className="w-6 h-6" />
              ) : (
                <TrashIcon className="w-6 h-6" />
              )}
            </button>
          </section>
          <div className={inputContainerClass}>
            <Input
              name={ACTIONS.PRICE}
              label="Precio (pagado por beneficiario)"
              type="number"
              step="0.01"
              value={formState.price}
              placeholder="Ingrese un precio"
              disabled={disabled}
              onChange={handleChangeNumber}
            />
          </div>
          <div className={inputContainerClass}>
            <Input
              name={ACTIONS.COMPARE_AT_PRICE}
              label="Precio comparativo"
              type="number"
              min={formState.compareAtPrice}
              step="0.01"
              value={formState.compareAtPrice}
              placeholder="Ingrese un monto"
              disabled={disabled}
              onChange={handleChangeNumber}
            />
          </div>
          <div className={inputContainerClass}>
            <Input
              name={ACTIONS.AGREED_PRICE}
              label="Precio (acordado con aliado)"
              type="number"
              step="0.01"
              value={formState.agreedPrice}
              placeholder="Ingrese un precio acordado"
              disabled={disabled}
              onChange={handleChangeNumber}
            />
          </div>
          <div className={inputContainerClass}>
            <Input
              name={ACTIONS.SKU}
              label="SKU"
              type="text"
              value={formState.sku}
              placeholder="Ingrese el SKU"
              disabled={disabled}
              onChange={handleChangeString}
            />
          </div>
          {!addNewCodes ? (
            <div className={inputContainerClass}>
              <Input
                name={ACTIONS.STOCK}
                label="Stock"
                type="text"
                value={formState.stock}
                placeholder="Ingrese stock"
                disabled={false}
                onChange={handleChangeNumber}
              />
            </div>
          ) : null}
        </div>
        {addNewCodes ? (
          <>
            <div className="w-[100%] border-t">
              <h1 className="px-10 py-5 text-lg text-neutral-400">
                Información de códigos{' '}
                <span className="text-[10px]">no autogenerados</span>
              </h1>
            </div>
            <div className="w-full relative px-5 grid md:grid-cols-1 justify-start items-start">
              <div className="w-full px-5 ">
                <div className="flex flex-col justify-center items-center">
                  <div className="w-full">
                    <ExcelForm
                      sheetName="Codigos"
                      updateData={handleExcelDocument}
                      documentFormat="https://avilatek.sfo3.digitaloceanspaces.com/images/Formato_Codes_Meit.xlsx"
                    />
                  </div>
                  <div className={`w-full  ${addNewCodes ? 'px-14 py-4' : ''}`}>
                    {/* Seccion de codigos especiales */}
                    {codesArray.length > 0 && addNewCodes ? (
                      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 justify-center items-center">
                        {codesArray.map((code, index) => (
                          <CodeForm
                            codeInfo={code}
                            key={index}
                            setCodesArray={setCodesArray}
                            index={index}
                          />
                        ))}
                      </section>
                    ) : null}
                    {codesArray.length === 0 && addNewCodes ? (
                      <div className="flex flex-col justify-center items-center">
                        <h1 className="text-danger-200">
                          Agregue un archivo excel para poder ver los códigos
                        </h1>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className=" w-full p-5 space-y-2">
                {/* EXPIRATION DATE */}
                <div className="space-x-2 mr-[6px]">
                  <span>Fecha de vencimiento</span>
                  <input
                    type="checkbox"
                    name={ACTIONS.EXPIRATION_DATE}
                    checked={expirationDate}
                    onChange={() => handleExpirationDateInputChange()}
                    className="w-4 h-4 cursor-pointer bg-white rounded border-purple-200 focus:ring-purple-300 focus:ring-2"
                  />
                </div>
                {expirationDate && expirationDate ? (
                  <div className="w-full">
                    <Input
                      name={ACTIONS.EXPIRATION_DATE}
                      value={
                        formState.expirationDate
                          ? formState.expirationDate.toISOString().split('T')[0]
                          : ''
                      }
                      type="date"
                      placeholder="Ingrese una fecha"
                      onChange={handleExpitationDateInputChange}
                      required
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </>
        ) : null}
      </Card.Body>
      <DocumentForm
        documents={documents}
        updateURLs={setDocuments}
        maximumAmount={5}
        title="Imagen del producto"
      />
    </>
  );
}

export default VariantForm;
